// =============================================================================
// VulnTracker — Dashboard Page
// =============================================================================
import { useState } from 'react';
import {
  ShieldExclamationIcon,
  CubeIcon,
  LinkIcon,
  BellIcon,
  ExclamationTriangleIcon,
  BuildingOffice2Icon,
  FolderIcon,
} from '@heroicons/react/24/outline';

/** Tiny "i" icon with hover tooltip used next to chart headings. */
function ChartInfo({ text }: { text: string }) {
  const [show, setShow] = useState(false);
  return (
    <span className="relative ml-2 inline-flex">
      <button
        type="button"
        className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-gray-200 text-gray-500 text-xs font-bold hover:bg-primary-100 hover:text-primary-700 focus:outline-none transition-colors"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow((p) => !p)}
        aria-label="More info"
      >
        i
      </button>
      {show && (
        <div className="absolute left-1/2 -translate-x-1/2 top-7 z-50 w-72 rounded-lg bg-gray-900 text-white text-xs leading-relaxed p-3 shadow-lg">
          <div className="absolute -top-1 left-1/2 -translate-x-1/2 h-2 w-2 rotate-45 bg-gray-900" />
          {text}
        </div>
      )}
    </span>
  );
}
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import { StatCard } from '@/components/StatCard';
import {
  useDashboardStats,
  useSeverityTrend,
  useEcosystemBreakdown,
  useRemediationStatus,
  useTopAffectedProjects,
} from '@/api/hooks';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

export function DashboardPage() {
  const { data: stats, isLoading: statsLoading } = useDashboardStats();
  const { data: trend } = useSeverityTrend(30);
  const { data: ecosystems } = useEcosystemBreakdown();
  const { data: remediation } = useRemediationStatus();
  const { data: topProjects } = useTopAffectedProjects(5);

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  // Safe array helper — guards against API returning HTML/object instead of array
  const safeArr = <T,>(val: T[] | unknown): T[] => (Array.isArray(val) ? val : []);

  // ── Severity trend chart data ──────────────────────────────────────
  const safeTrend = safeArr<{ date: string; critical: number; high: number; medium: number; low: number }>(trend);
  const trendChartData = {
    labels: safeTrend.map((t) => t.date),
    datasets: [
      { label: 'Critical', data: safeTrend.map((t) => t.critical), backgroundColor: '#dc2626' },
      { label: 'High',     data: safeTrend.map((t) => t.high),     backgroundColor: '#ea580c' },
      { label: 'Medium',   data: safeTrend.map((t) => t.medium),   backgroundColor: '#ca8a04' },
      { label: 'Low',      data: safeTrend.map((t) => t.low),      backgroundColor: '#2563eb' },
    ],
  };

  // ── Ecosystem doughnut chart ───────────────────────────────────────
  const safeEco = safeArr<{ ecosystem: string; count: number }>(ecosystems);
  const ecosystemChartData = {
    labels: safeEco.map((e) => e.ecosystem),
    datasets: [
      {
        data: safeEco.map((e) => e.count),
        backgroundColor: [
          '#3b82f6', '#ef4444', '#f59e0b', '#10b981',
          '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#6366f1',
        ],
      },
    ],
  };

  // ── Remediation bar chart ──────────────────────────────────────────
  const safeRem = safeArr<{ status: string; count: number }>(remediation);
  const remediationChartData = {
    labels: safeRem.map((r) => r.status.replace('_', ' ')),
    datasets: [
      {
        label: 'Count',
        data: safeRem.map((r) => r.count),
        backgroundColor: '#3b82f6',
        borderRadius: 4,
      },
    ],
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Security posture overview for your organization
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Organizations"
          value={stats?.total_organizations ?? 0}
          icon={<BuildingOffice2Icon className="h-6 w-6 text-primary-600" />}
          info="Total number of organizations registered in VulnTracker. Each organization can contain multiple projects."
        />
        <StatCard
          title="Total Projects"
          value={stats?.total_projects ?? 0}
          icon={<FolderIcon className="h-6 w-6 text-primary-600" />}
          info="Total count of projects linked to organizations. Only projects belonging to an organization are counted."
        />
        <StatCard
          title="Total Components"
          value={stats?.total_components ?? 0}
          icon={<CubeIcon className="h-6 w-6 text-primary-600" />}
          info="Total count of software components (libraries, packages, dependencies) across all organization-linked projects."
        />
        <StatCard
          title="Total Vulnerabilities"
          value={stats?.total_vulnerabilities ?? 0}
          icon={<ShieldExclamationIcon className="h-6 w-6 text-primary-600" />}
          info="Total count of distinct vulnerabilities matched to components in organization-linked projects. Only vulnerabilities affecting your tracked components are counted."
        />
      </div>

      {/* Severity breakdown row */}
      <div className="grid grid-cols-5 gap-4">
        {[
          { label: 'Critical', value: stats?.critical_count ?? 0, color: 'bg-red-500' },
          { label: 'High', value: stats?.high_count ?? 0, color: 'bg-orange-500' },
          { label: 'Medium', value: stats?.medium_count ?? 0, color: 'bg-yellow-500' },
          { label: 'Low', value: stats?.low_count ?? 0, color: 'bg-blue-500' },
          { label: 'None / Unknown', value: stats?.none_count ?? 0, color: 'bg-gray-400' },
        ].map(({ label, value, color }) => (
          <div key={label} className="card text-center">
            <div className={`mx-auto h-3 w-3 rounded-full ${color} mb-2`} />
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-sm text-gray-500">{label}</p>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Severity trend */}
        <div className="card">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            Vulnerability Trend (30 days)
            <ChartInfo text="Shows the number of new vulnerabilities published each day over the last 30 days, grouped by severity (Critical, High, Medium, Low). Based on each vulnerability's published date." />
          </h3>
          <div className="h-72">
            <Bar
              data={trendChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  x: { stacked: true },
                  y: { stacked: true, beginAtZero: true },
                },
                plugins: { legend: { position: 'bottom' } },
              }}
            />
          </div>
        </div>

        {/* Ecosystem breakdown */}
        <div className="card">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            Components by Ecosystem
            <ChartInfo text="Breaks down all tracked components by their package ecosystem (e.g. npm, PyPI, Maven, Go, Cargo). Helps identify which technology stacks are most prevalent in your projects." />
          </h3>
          <div className="h-72 flex items-center justify-center">
            <Doughnut
              data={ecosystemChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { position: 'right' } },
              }}
            />
          </div>
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Remediation status */}
        <div className="card">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            Remediation Status
            <ChartInfo text="Shows the current remediation state of all vulnerability matches: Not Started, In Progress, Completed, Deferred, or Accepted Risk. Helps track how effectively vulnerabilities are being addressed." />
          </h3>
          <div className="h-64">
            <Bar
              data={remediationChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: { y: { beginAtZero: true } },
                plugins: { legend: { display: false } },
              }}
            />
          </div>
        </div>

        {/* Top affected projects */}
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Top Affected Projects</h3>
          <div className="space-y-3">
            {safeArr<{ project_id: string; project_name: string; match_count: number; critical_count: number; high_count: number; medium_count: number; low_count: number; none_count: number }>(topProjects).map((p) => (
              <div
                key={p.project_id}
                className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
              >
                <div>
                  <p className="font-medium text-gray-900">{p.project_name}</p>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    {p.critical_count > 0 && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-red-500 inline-block" />
                        {p.critical_count} critical
                      </span>
                    )}
                    {p.high_count > 0 && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-orange-500 inline-block" />
                        {p.high_count} high
                      </span>
                    )}
                    {p.medium_count > 0 && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-yellow-500 inline-block" />
                        {p.medium_count} medium
                      </span>
                    )}
                    {p.low_count > 0 && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-blue-500 inline-block" />
                        {p.low_count} low
                      </span>
                    )}
                    {p.none_count > 0 && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                        <span className="h-1.5 w-1.5 rounded-full bg-gray-400 inline-block" />
                        {p.none_count} none
                      </span>
                    )}
                  </div>
                </div>
                <span className="badge bg-gray-100 text-gray-800 ml-3 shrink-0">
                  {p.match_count} total
                </span>
              </div>
            ))}
            {(!topProjects || topProjects.length === 0) && (
              <p className="text-sm text-gray-500">No affected projects</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
